interface PasswordRules {
    ErrorMessage: string;
    InstanceIdentifier: string;
    MinimumLength: string;
    RequireCapital: boolean;
    RequireNumber: boolean;
    RequireSpecialCharacter: boolean;
}
export default class PasswordValidation {
    inputElement: HTMLInputElement;
    inputElementContainer: HTMLDivElement;
    passwordRules: PasswordRules;
    showPasswordButton: HTMLButtonElement;

    constructor(private inputID: string, passwordRulesJSON: string) {

        try {
            this.inputElement = document.getElementById(this.inputID) as HTMLInputElement;
            this.inputElementContainer = this.inputElement.closest<HTMLDivElement>('.form-field');
            this.passwordRules = JSON.parse(passwordRulesJSON) as PasswordRules;

            // Create the toggle visibility icon
            this.showPasswordButton = document.createElement('button');
            this.showPasswordButton.innerHTML = '<span class="dot-icon dot-stroke-view"></span><span class="visually-hidden">Show Password</span>';
            this.showPasswordButton.classList.add('show-password-button');
            this.showPasswordButton.addEventListener('click', (e) => this.togglePasswordVisibility(e));
            this.inputElement.parentElement.classList.add('password-field');;
            this.inputElement.insertAdjacentElement('afterend', this.showPasswordButton);

            this.inputElement.addEventListener('input', () => this.validatePassword());

        } catch {
            throw new Error("Error parsing JSON");
        }

    }

    validatePassword(): void {
        const inputValue = this.inputElement.value;

        if (
            !this.passwordRules.RequireCapital &&
            !this.passwordRules.RequireNumber &&
            !this.passwordRules.RequireSpecialCharacter &&
            (!this.passwordRules.MinimumLength || Number(this.passwordRules.MinimumLength) === 0)
        ) {
            // None of the rules are enabled, so no need to display validation
            this.removeValidationElement();
            return;
        }

        const validationResults = this.validatePasswordRules(inputValue);

        // Remove existing icons and messages
        this.removeValidationElement();

        const validationContainer = document.createElement('div');
        validationContainer.classList.add('validation-container');

        const validationText = document.createElement('p');
        validationText.textContent = "Your password must fulfil the following requirements:";

        validationContainer.appendChild(validationText);

        const ruleMessages: Record<string, (length: string | boolean) => string> = {
            MinimumLength: (length) => `At least ${length} characters`,
            RequireCapital: () => "At least one uppercase letter",
            RequireNumber: () => "At least one number",
            RequireSpecialCharacter: () => "At least one special character",
        };

        // Iterate through validation results and create messages with icons
        for (const rule in validationResults) {
            if (this.passwordRules[rule as keyof PasswordRules]) {

                const validationRule = document.createElement('div');
                validationRule.classList.add('validation-rule');

                const icon = document.createElement('span');
                const message = document.createElement('span');
                const result = validationResults[rule];

                icon.classList.add('validation-icon');
                message.classList.add('validation-message');

                if (result.isValid) {
                    icon.classList.add('valid', 'dot-icon', 'dot-stroke-check');
                    message.textContent = ` ${ruleMessages[rule](this.passwordRules[rule as keyof PasswordRules])}`;
                    message.classList.add('valid');
                } else {
                    icon.classList.add('invalid', 'dot-icon', 'dot-stroke-close');
                    message.textContent = ` ${ruleMessages[rule](this.passwordRules[rule as keyof PasswordRules])}`;
                    message.classList.add('invalid');
                }

                validationRule.appendChild(icon);
                validationRule.appendChild(message);

                validationContainer.appendChild(validationRule);
            }
        }
        this.inputElementContainer.appendChild(validationContainer);
    }

    // Remove existing validation visuals
    removeValidationElement(): void {
        const validationContainer = this.inputElementContainer.querySelector('.validation-container');

        if (validationContainer)
            validationContainer.remove();
    }

    // Validate password
    validatePasswordRules(password: string): Record<string, { isValid: boolean; message: string }> {
        const results: Record<string, { isValid: boolean; message: string }> = {};

        results.MinimumLength = {
            isValid: password.length >= parseInt(this.passwordRules.MinimumLength),
            message: `Password should be at least ${this.passwordRules.MinimumLength} characters long`,
        };

        results.RequireCapital = {
            isValid: this.passwordRules.RequireCapital ? /[A-Z]/.test(password) : true,
            message: 'Password should contain at least one capital letter',
        };

        results.RequireNumber = {
            isValid: this.passwordRules.RequireNumber ? /\d/.test(password) : true,
            message: 'Password should contain at least one number',
        };

        results.RequireSpecialCharacter = {
            isValid: this.passwordRules.RequireSpecialCharacter
                ? /[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/.test(password)
                : true,
            message: 'Password should contain at least one special character',
        };

        return results;
    }

    togglePasswordVisibility(e: Event): void {
        e.preventDefault();
        const type = this.inputElement.type === 'password' ? 'text' : 'password';
        this.inputElement.type = type;
    }
}